import React from 'react';

export const useElementWidth = <T extends HTMLElement>(): [(node: T | null) => void, number] => {
    const [node, setNode] = React.useState<T | null>(null);
    const [width, setWidth] = React.useState(0);

    const ref = React.useCallback((next: T | null) => setNode(next), []);

    React.useLayoutEffect(() => {
        if (!node) return undefined;

        const measure = () => setWidth((current) => {
            const next = node.getBoundingClientRect().width;
            return Math.abs(next - current) < 1 ? current : next;
        });

        measure();

        let frame = 0;
        const schedule = () => { if (!frame) frame = window.requestAnimationFrame(() => { frame = 0; measure(); }); };

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', schedule);
            return () => {
                if (frame) window.cancelAnimationFrame(frame);
                window.removeEventListener('resize', schedule);
            };
        }

        const observer = new ResizeObserver(schedule);
        observer.observe(node);
        return () => {
            if (frame) window.cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, [node]);

    return [ref, width];
};
